import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Plus, X, Edit2, Check, Trash2, Save, GitMerge, Circle, Sparkles } from 'lucide-react';
import { api } from '../../api/client';
import { useAppContext } from '../../context/AppContext';

// ---- Layout constants ----------------------------------------------------------------------------------------------------------------------
const W = 540, H = 340, R = 130;
const CX = [185, 355], CY = [170, 170];
// Unique-region centroids and intersection centroid
const REGION_CENTROIDS = {
  a:  { x: 130, y: 170 },
  b:  { x: 410, y: 170 },
  ab: { x: 270, y: 170 },
};
const CIRCLE_COLORS = [
  { stroke: '#01006A', fill: 'rgba(1,0,106,0.10)', label: '#010059' },
  { stroke: '#0ea5e9', fill: 'rgba(14,165,233,0.13)',  label: '#0369a1' },
];
const INTER_FILL   = 'rgba(1,0,106,0.07)';
const INTER_ACTIVE = 'rgba(1,0,106,0.20)';
const REGION_HOVER = 'rgba(1,0,106,0.15)';

const SAVE_DEBOUNCE_MS = 1200;

export default function SessionVennView({ writingSession, sections }) {
  const { currentUser } = useAppContext();
  const canEdit = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  const [sectionA, setSectionA] = useState(null);  // full section object or null
  const [sectionB, setSectionB] = useState(null);
  const [vennData, setVennData] = useState({ a: [], b: [], ab: [] });
  const [openDropdown, setOpenDropdown] = useState(null);  // 'a' | 'b' | null
  const [activeRegion, setActiveRegion] = useState(null);  // 'a' | 'b' | 'ab'
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [newCapability, setNewCapability] = useState('');
  const [editingItem, setEditingItem] = useState(null);  // { region, idx }
  const [editedText, setEditedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const svgRef = useRef(null);
  const saveTimerRef = useRef(null);
  const isLoadedRef = useRef(false);

  // Sections that have takeaways (for dropdowns)
  const sectionsWithTakeaways = sections.filter(s => s.takeaways && s.takeaways.length > 0);

  // ---- Load saved state on mount ------------------------------------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const state = await api.getSessionVenn(writingSession.id);
        const secA = sections.find(s => s.id === state.section_a_id) || null;
        const secB = sections.find(s => s.id === state.section_b_id) || null;
        setSectionA(secA);
        setSectionB(secB);
        setVennData({
          a:  (state.venn_data?.a  || (secA ? [...secA.takeaways] : [])),
          b:  (state.venn_data?.b  || (secB ? [...secB.takeaways] : [])),
          ab: (state.venn_data?.ab || []),
        });
      } catch {
        // No saved state yet -- that's fine
      }
      isLoadedRef.current = true;
    };
    load();
  }, [writingSession.id]);  // eslint-disable-line

  // ---- Debounced auto-save whenever vennData / selections change --------------------------
  const scheduleSave = useCallback((data, secA, secB) => {
    if (!isLoadedRef.current || !canEdit) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        await api.saveSessionVenn(writingSession.id, {
          section_a_id: secA?.id || null,
          section_b_id: secB?.id || null,
          venn_data: data,
        });
        setLastSaved(new Date());
      } catch (e) {
        console.error('Failed to save venn:', e);
      } finally {
        setIsSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [writingSession.id, canEdit]);

  // ---- Section selection ----------------------------------------------------------------------------------------------------------------
  const handleSelectSection = (circle, section) => {
    const newA = circle === 'a' ? section : sectionA;
    const newB = circle === 'b' ? section : sectionB;
    const newData = {
      ...vennData,
      [circle]: section ? [...section.takeaways] : [],
      ab: [],  // clear intersection when selections change
    };
    if (circle === 'a') setSectionA(section);
    else setSectionB(section);
    setVennData(newData);
    setOpenDropdown(null);
    scheduleSave(newData, newA, newB);
  };

  // ---- Capability editing --------------------------------------------------------------------------------------------------------------
  const updateData = (region, items) => {
    const newData = { ...vennData, [region]: items };
    setVennData(newData);
    scheduleSave(newData, sectionA, sectionB);
  };

  const addCapability = () => {
    if (!newCapability.trim() || !activeRegion) return;
    updateData(activeRegion, [...vennData[activeRegion], newCapability.trim()]);
    setNewCapability('');
  };

  const deleteCapability = (region, idx) => {
    updateData(region, vennData[region].filter((_, i) => i !== idx));
  };

  const saveEdit = (region, idx) => {
    updateData(region, vennData[region].map((t, i) => i === idx ? editedText : t));
    setEditingItem(null);
  };

  const moveToIntersection = (fromRegion, idx) => {
    const item = vennData[fromRegion][idx];
    const newFrom = vennData[fromRegion].filter((_, i) => i !== idx);
    const newAb = [...vennData.ab, item];
    const newData = { ...vennData, [fromRegion]: newFrom, ab: newAb };
    setVennData(newData);
    scheduleSave(newData, sectionA, sectionB);
  };

  const handleAnalyze = async () => {
    if (!sectionA || !sectionB) return;
    setIsAnalyzing(true);
    try {
      const result = await api.compareVenn(sectionA.id, sectionB.id);
      const newData = { a: result.a, b: result.b, ab: result.ab };
      setVennData(newData);
      scheduleSave(newData, sectionA, sectionB);
      setActiveRegion('ab'); // open the intersection panel so user sees the result immediately
    } catch (e) {
      alert('Failed to analyze sections: ' + e.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ---- SVG hit-testing --------------------------------------------------------------------------------------------------------------------
  const getRegionAtPoint = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());
    const inA = (x-CX[0])**2 + (y-CY[0])**2 <= R*R;
    const inB = (x-CX[1])**2 + (y-CY[1])**2 <= R*R;
    if (inA && inB) return 'ab';
    if (inA) return 'a';
    if (inB) return 'b';
    return null;
  };

  const regionFill = (region) => {
    if (region === activeRegion)  return INTER_ACTIVE;
    if (region === hoveredRegion) return REGION_HOVER;
    return region === 'ab' ? INTER_FILL : 'transparent';
  };

  // ---- Region label --------------------------------------------------------------------------------------------------------------------------
  const regionLabel = (region) => {
    if (region === 'a') return sectionA?.title || 'Circle A';
    if (region === 'b') return sectionB?.title || 'Circle B';
    return 'Shared';
  };

  const truncate = (s, n = 18) => s.length > n ? s.slice(0, n-1) + '…' : s;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left -- SVG diagram + section selectors */}
      <div className="flex flex-col items-center p-6 flex-shrink-0 w-[560px] border-r border-gray-100 bg-white overflow-y-auto">
        <h3 className="text-base font-bold text-gray-800 mb-4 self-start">Session Capability Map</h3>

        {/* Selectors */}
        <div className="grid grid-cols-2 gap-3 w-full mb-5">
          {(['a', 'b']).map((circle, i) => {
            const selected = circle === 'a' ? sectionA : sectionB;
            return (
              <div key={circle} className="relative">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CIRCLE_COLORS[i].stroke }} />
                  <span className="text-xs font-semibold text-gray-600">Circle {circle.toUpperCase()}</span>
                </div>
                <button onClick={() => setOpenDropdown(openDropdown === circle ? null : circle)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#6b6bc2] text-xs transition-colors">
                  <span className={selected ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                    {selected ? truncate(selected.title, 22) : 'Select a section…'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                </button>
                {openDropdown === circle && (
                  <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    <div className="max-h-48 overflow-y-auto">
                      <button onClick={() => handleSelectSection(circle, null)}
                        className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 border-b border-gray-100">
                        -- None --
                      </button>
                      {sectionsWithTakeaways.map(sec => (
                        <button key={sec.id}
                          onClick={() => handleSelectSection(circle, sec)}
                          disabled={(circle === 'a' ? sectionB : sectionA)?.id === sec.id}
                          className="w-full text-left px-3 py-2 hover:bg-[#e6e6f5] disabled:opacity-40 disabled:cursor-not-allowed">
                          <div className="text-xs font-medium text-gray-800">{sec.title}</div>
                          <div className="text-xs text-gray-400">{sec.takeaways.length} takeaways</div>
                        </button>
                      ))}
                      {sectionsWithTakeaways.length === 0 && (
                        <div className="px-3 py-5 text-center text-xs text-gray-400">
                          No sections with takeaways yet.<br />Use the Transcript tab to extract some.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* AI analysis button */}
        {canEdit && (
          <div className="w-full mb-4">
            <button
              onClick={handleAnalyze}
              disabled={!sectionA || !sectionB || isAnalyzing}
              className="w-full flex items-center justify-center gap-2 bg-[#01006A] text-white py-2.5 rounded-xl hover:bg-[#010059] disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors">
              <Sparkles className="w-4 h-4" />
              {isAnalyzing ? 'Analyzing similarities…' : 'AI: Find Shared Capabilities'}
            </button>
            {(!sectionA || !sectionB) && (
              <p className="text-xs text-gray-400 text-center mt-1">Select both sections to enable</p>
            )}
            {sectionA && sectionB && !isAnalyzing && (
              <p className="text-xs text-gray-400 text-center mt-1">Replaces current diagram with AI analysis</p>
            )}
          </div>
        )}

        {/* SVG Venn */}
        <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
          className="cursor-pointer select-none"
          onClick={e => { const r = getRegionAtPoint(e); if (r) setActiveRegion(r === activeRegion ? null : r); }}
          onMouseMove={e => setHoveredRegion(getRegionAtPoint(e))}
          onMouseLeave={() => setHoveredRegion(null)}>

          {/* Circle fills */}
          {[0,1].map(i => (
            <circle key={i} cx={CX[i]} cy={CY[i]} r={R}
              fill={CIRCLE_COLORS[i].fill} stroke={CIRCLE_COLORS[i].stroke} strokeWidth={2} />
          ))}

          {/* Hover / active overlays */}
          {/* A-only region */}
          <clipPath id="clipA"><circle cx={CX[0]} cy={CY[0]} r={R} /></clipPath>
          <clipPath id="clipB"><circle cx={CX[1]} cy={CY[1]} r={R} /></clipPath>
          {/* Exclude-B mask for A-only */}
          <rect x={0} y={0} width={W} height={H} fill={regionFill('a')} clipPath="url(#clipA)" />
          {/* Exclude-A mask for B-only -- draw B then blank-out A */}
          <rect x={0} y={0} width={W} height={H} fill={regionFill('b')} clipPath="url(#clipB)" />
          {/* Intersection overlay */}
          <g clipPath="url(#clipA)">
            <circle cx={CX[1]} cy={CY[1]} r={R} fill={regionFill('ab')} />
          </g>

          {/* Count badges */}
          {(['a','b','ab']).map(region => {
            const n = vennData[region].length;
            if (!n) return null;
            const { x, y } = REGION_CENTROIDS[region];
            return (
              <g key={region}>
                <circle cx={x} cy={y} r={11} fill={activeRegion === region ? '#01006A' : '#6b7280'} />
                <text x={x} y={y+4} textAnchor="middle" fontSize={10} fill="white" fontWeight="bold">{n}</text>
              </g>
            );
          })}

          {/* Circle labels */}
          {[0,1].map(i => {
            const sec = i === 0 ? sectionA : sectionB;
            return (
              <text key={i} x={CX[i]} y={CY[i] - R - 8} textAnchor="middle"
                fontSize={11} fontWeight={600} fill={CIRCLE_COLORS[i].label}>
                {sec ? truncate(sec.title, 20) : `Circle ${['A','B'][i]}`}
              </text>
            );
          })}

          <text x={W/2} y={H-6} textAnchor="middle" fontSize={10} fill="#94a3b8">
            Click a region to view & edit capabilities
          </text>
        </svg>

        {/* Save status */}
        <div className="mt-2 text-xs text-gray-400 self-end">
          {isSaving ? 'Saving…' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : ''}
        </div>
      </div>

      {/* Right -- editor panel */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
        {!activeRegion ? (
          <div className="flex-1 flex items-center justify-center text-center text-gray-400 p-8">
            <div>
              <GitMerge className="w-12 h-12 mx-auto mb-3 opacity-25" />
              <p className="text-sm font-medium">Click a region on the diagram</p>
              <p className="text-xs mt-1 text-gray-400">to view and edit its capabilities</p>
            </div>
          </div>
        ) : (
          <>
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100 flex-shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  {activeRegion === 'ab'
                    ? <GitMerge className="w-4 h-4 text-[#01006A]" />
                    : <Circle className="w-4 h-4" style={{ color: CIRCLE_COLORS[activeRegion === 'a' ? 0 : 1].stroke }} />
                  }
                  <h4 className="font-bold text-gray-900 text-sm">{regionLabel(activeRegion)}</h4>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {activeRegion === 'ab' ? 'Capabilities shared between both circles'
                    : activeRegion === 'a' ? 'Unique to Circle A'
                    : 'Unique to Circle B'}
                </p>
              </div>
              <button onClick={() => setActiveRegion(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {vennData[activeRegion].length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <Circle className="w-8 h-8 mx-auto mb-2 opacity-25" />
                  <p className="text-xs">No capabilities yet. Add one below.</p>
                </div>
              )}
              {vennData[activeRegion].map((item, idx) => (
                <div key={idx}
                  className="flex items-start gap-2 p-3 bg-white border border-gray-200 rounded-xl group hover:border-[#9696d4] transition-colors">
                  <span className="text-xs font-bold text-gray-300 mt-0.5 flex-shrink-0">{idx+1}</span>
                  {editingItem?.region === activeRegion && editingItem?.idx === idx ? (
                    <div className="flex-1">
                      <textarea value={editedText} onChange={e => setEditedText(e.target.value)}
                        className="w-full text-xs border border-[#6b6bc2] rounded p-1.5 resize-none bg-white focus:ring-1 focus:ring-[#4d4db5]"
                        rows="2" autoFocus />
                      <div className="flex gap-2 mt-1">
                        <button onClick={() => saveEdit(activeRegion, idx)}
                          className="text-xs text-[#01006A] font-medium hover:text-[#010059] flex items-center gap-0.5">
                          <Check className="w-3 h-3" />Save
                        </button>
                        <button onClick={() => setEditingItem(null)}
                          className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-gray-800 flex-1 leading-relaxed">{item}</p>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {canEdit && activeRegion !== 'ab' && (
                          <button onClick={() => moveToIntersection(activeRegion, idx)}
                            title="Move to intersection"
                            className="p-1 text-[#6b6bc2] hover:text-[#01006A] rounded text-xs font-medium">
                            ↔
                          </button>
                        )}
                        {canEdit && (
                          <>
                            <button onClick={() => { setEditingItem({ region: activeRegion, idx }); setEditedText(item); }}
                              className="p-1 text-gray-300 hover:text-[#01006A] rounded">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={() => deleteCapability(activeRegion, idx)}
                              className="p-1 text-gray-300 hover:text-red-500 rounded">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Add new capability */}
            {canEdit && (
              <div className="flex-shrink-0 p-4 bg-white border-t border-gray-100">
                <div className="flex gap-2">
                  <input value={newCapability} onChange={e => setNewCapability(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCapability()}
                    placeholder={`Add capability to ${regionLabel(activeRegion)}…`}
                    className="flex-1 text-xs px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#01006A] focus:outline-none" />
                  <button onClick={addCapability} disabled={!newCapability.trim()}
                    className="p-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] disabled:bg-gray-200 transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {activeRegion !== 'ab' && vennData[activeRegion].length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Tip: click ↔ on any item to move it to the shared intersection.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
